import type { ErrorLike } from "@apollo/client";
import { Box, Row, SeparatorLine, Space, Txt } from "@stenajs-webui/core";
import { ScreenSpinner } from "@stenajs-webui/elements";
import { cssColor } from "@stenajs-webui/theme";
import type { FC } from "react";

import type { SearchBookingsResult } from "../../../../common/graphql/useSearchBookingsResult";
import { Errors, Information } from "../../../../common/texts";
import type { SearchResultColumn } from "../utils/searchResultColumns";
import { BookingSearchTable } from "./BookingSearchTable";
import { BookingSearchTableHeaderRow } from "./BookingSearchTableHeaderRow";

interface BookingSearchListInnerContentProps {
  data: SearchBookingsResult;
  error?: ErrorLike;
  loading: boolean;
  onClickResult: (bookingNo: number) => void;
  onHistoryClick: () => void;
  onTransportClick: () => void;
  searchColumns: SearchResultColumn[];
  searchQuery: string;
}

export const BookingSearchListInnerContent: FC<
  BookingSearchListInnerContentProps
> = ({
  onClickResult,
  data,
  loading,
  searchQuery,
  error,
  onHistoryClick,
  onTransportClick,
  searchColumns,
}) => {
  if (loading) {
    return (
      <Row spacing={2} indent={2} justifyContent={"center"}>
        <ScreenSpinner />
      </Row>
    );
  }

  if (error) {
    return (
      <Row spacing={2} indent={2} className={"t_search_error"}>
        <Txt color={cssColor("--lhds-color-red-500")}>
          {Errors.SomethingWentWrongTryAgain}
        </Txt>
      </Row>
    );
  }

  if (searchQuery.length === 0) {
    return (
      <Row spacing={2} indent={2} className={"t_search_input_empty"}>
        <Txt>{Information.SearchPlaceholderHint}</Txt>
      </Row>
    );
  }

  return (
    <>
      {data.historicTransportsCount === 0 &&
      data.activeTransportsCount === 0 ? (
        <Row spacing={2} indent={2} className={"t_search_empty"}>
          <Txt>{Information.SearchNoResults}</Txt>
        </Row>
      ) : (
        <>
          <Box display={["none", "flex"]}>
            <BookingSearchTableHeaderRow searchColumns={searchColumns} />
            <SeparatorLine color={cssColor("--lhds-color-ui-300")} />
          </Box>
          {data.activeTransportsCount > 0 && (
            <BookingSearchTable
              searchColumns={searchColumns}
              onClickResult={onClickResult}
              linkClassName={"t_search_planned_transports"}
              items={data.activeTransportsItems}
              label={"Planned Transports"}
              onTotalCountClick={onTransportClick}
              totalCount={data.activeTransportsCount}
              searchQuery={searchQuery}
            />
          )}
          {data.historicTransportsCount > 0 && (
            <>
              {data.activeTransportsCount > 0 && (
                <>
                  <Space />
                  <SeparatorLine color={cssColor("--lhds-color-ui-300")} />
                  <Space />
                </>
              )}
              <BookingSearchTable
                searchColumns={searchColumns}
                onClickResult={onClickResult}
                items={data.historicTransportsItems}
                label={"History"}
                linkClassName={"t_search_history_transports"}
                onTotalCountClick={onHistoryClick}
                totalCount={data.historicTransportsCount}
                searchQuery={searchQuery}
              />
            </>
          )}
        </>
      )}
    </>
  );
};
